import React, { useMemo, useState } from 'react';
import { ChevronDown, FileText } from 'lucide-react';
import {
  agruparPorAnio, aniosAbiertos, diaMes, RIESGO_LABEL, RIESGO_PUNTO, riesgoMaximo, separarFuente, tipoEvaluacionLabel,
} from 'lib/arboles';
import { fechaCorta, fechaHoraCorta, nombreUsuario, plural } from 'lib/auditoria';
import { MarcaAnulada } from 'components/EstadoPill';
import { RiesgoDiscreto } from 'components/RiesgoPill';
import Button from 'components/ui/Button';
import { BotonArchivo } from './FichaPartes';

// Un campo se recorta a 3 líneas si pasa de este largo; "Ver más" lo abre.
const LARGO = 180;

const evaluador = (e) => [
  e.evaluador_nombre || 'Sin nombre',
  e.evaluador_profesion,
  e.evaluador_colegiado,
].filter(Boolean).join(', ');

const enlace = 'rounded font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

// Una evaluación en la línea de tiempo. Compacta (los seguimientos que no son
// la última): encabezado y un resumen de una línea; "Ver detalle" la abre.
function Evaluacion({ e, compacta, esAdmin, onAnular, nombres }) {
  const [detalle, setDetalle] = useState(!compacta);
  const [abierta, setAbierta] = useState(false);
  const rec = separarFuente(e.recomendaciones);
  const acc = separarFuente(e.acciones);
  const fuentes = [rec.fuente, acc.fuente].filter(Boolean);
  const filas = [
    ['Raíces', e.raices], ['Tronco', e.tronco], ['Copa', e.copa], ['Plagas o enfermedades', e.plagas_enfermedades],
    ['Inclinación', e.inclinacion_grados === null || e.inclinacion_grados === undefined || e.inclinacion_grados === '' ? null : `${e.inclinacion_grados}°`],
    ['Recomendaciones', rec.texto], ['Acciones', acc.texto],
  ].filter(([, v]) => v);
  const larga = filas.some(([, v]) => String(v).length > LARGO);
  const resumen = filas.find(([k]) => k === 'Acciones') || filas.find(([k]) => k === 'Recomendaciones') || filas[0];
  const hayDetalle = filas.length > 0 || fuentes.length > 0;
  return (
    <li data-testid="evaluacion" className={`relative pl-7 ${detalle ? 'pb-7' : 'pb-4'} last:pb-1`}>
      <span aria-hidden="true"
        className={`absolute left-0 top-1.5 h-3 w-3 rounded-full ring-4 ring-card ${
          e.anulada || !RIESGO_PUNTO[e.riesgo] ? 'border-2 border-muted-foreground/50 bg-card' : RIESGO_PUNTO[e.riesgo]}`} />
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className={`font-heading text-[15px] font-bold tabular-nums text-foreground ${e.anulada ? 'line-through decoration-muted-foreground/60' : ''}`}
          title={fechaCorta(e.fecha)}>
          {diaMes(e.fecha) || 'Sin fecha'}
        </span>
        <span className="text-sm text-muted-foreground">{tipoEvaluacionLabel(e.tipo)}</span>
        <span className="ml-auto flex items-center gap-2">
          {e.anulada && <MarcaAnulada />}
          <RiesgoDiscreto riesgo={e.riesgo} textoVacio="Sin clasificar" />
        </span>
      </div>
      <p className="mt-0.5 text-[13px] text-muted-foreground">{evaluador(e)}</p>

      {!detalle && resumen && (
        <p className="mt-1.5 line-clamp-2 text-sm text-foreground">
          <span className="text-muted-foreground">{resumen[0]}: </span>{resumen[1]}
        </p>
      )}

      {detalle && filas.length > 0 && (
        <dl className={`mt-2.5 divide-y divide-border/70 rounded-lg border border-border/80 ${e.anulada ? 'opacity-70' : ''}`}>
          {filas.map(([k, v]) => (
            <div key={k} className="grid gap-0.5 px-3 py-2 sm:grid-cols-[9.5rem_minmax(0,1fr)] sm:gap-3">
              <dt className="text-xs font-medium text-muted-foreground sm:pt-0.5">{k}</dt>
              <dd className={`whitespace-pre-line text-sm leading-relaxed text-foreground ${!abierta && String(v).length > LARGO ? 'line-clamp-3' : ''}`}>{v}</dd>
            </div>
          ))}
        </dl>
      )}

      {detalle && fuentes.map((f) => (
        <p key={f} className="mt-2 flex gap-1.5 text-xs leading-snug text-muted-foreground">
          <FileText size={13} className="mt-px shrink-0" aria-hidden="true" />
          <span><span className="sr-only">Fuente: </span>{f}</span>
        </p>
      ))}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
        {!detalle && hayDetalle && (
          <button type="button" onClick={() => setDetalle(true)} aria-expanded={false} className={enlace}>Ver detalle</button>
        )}
        {detalle && larga && (
          <button type="button" onClick={() => setAbierta((v) => !v)} aria-expanded={abierta} className={enlace}>
            {abierta ? 'Ver menos' : 'Ver más'}
          </button>
        )}
        {detalle && compacta && (
          <button type="button" onClick={() => { setDetalle(false); setAbierta(false); }} aria-expanded className={enlace}>Ocultar detalle</button>
        )}
        <span>{e.proxima_evaluacion ? `Próxima: ${fechaCorta(e.proxima_evaluacion)}` : 'Sin próxima evaluación'}</span>
        {detalle && e.created_by && <span>Registrada por {nombreUsuario(e.created_by, nombres)}</span>}
        {e.informe_path && <BotonArchivo path={e.informe_path}>Informe PDF</BotonArchivo>}
        {esAdmin && !e.anulada && (
          <Button type="button" variant="ghost" size="xs" className="ml-auto text-muted-foreground hover:bg-error/10 hover:text-error"
            onClick={() => onAnular(e)}>
            Anular
          </Button>
        )}
      </div>
      {e.anulada && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Anulada{e.anulada_por ? ` por ${nombreUsuario(e.anulada_por, nombres)}` : ''}{e.anulada_at ? ` el ${fechaHoraCorta(e.anulada_at)}` : ''}: {e.anulada_motivo || 'sin motivo'}. No cuenta para el riesgo.
        </p>
      )}
    </li>
  );
}

// Evaluaciones como línea de tiempo por año (más nueva arriba). Arrancan
// abiertos el año actual y los que tienen las 3 más recientes; el resto
// queda plegado para que 30+ informes no hagan una página interminable.
export default function LineaEvaluaciones({ evaluaciones, hoy, esAdmin, onAnular, nombres }) {
  const grupos = useMemo(() => agruparPorAnio(evaluaciones), [evaluaciones]);
  const iniciales = useMemo(() => aniosAbiertos(grupos, hoy), [grupos, hoy]);
  const [cambios, setCambios] = useState({});
  const primera = evaluaciones?.[0]?.id;
  const abierto = (anio) => (anio in cambios ? cambios[anio] : iniciales.has(anio));
  return (
    <div className="space-y-1">
      {grupos.map((g) => {
        const open = abierto(g.anio);
        const max = riesgoMaximo(g.evaluaciones);
        const panel = `anio-${g.anio}`;
        return (
          <div key={g.anio} data-testid="anio-evaluaciones">
            <h3>
              <button type="button" aria-expanded={open} aria-controls={panel}
                onClick={() => setCambios((c) => ({ ...c, [g.anio]: !open }))}
                className="flex w-full items-center gap-3 rounded-lg px-1 py-2 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="font-heading text-lg font-bold tabular-nums text-foreground">{g.anio}</span>
                <span className="text-sm text-muted-foreground">{plural(g.evaluaciones.length, 'evaluación', 'evaluaciones')}</span>
                {max && !open && (
                  <span className="hidden text-xs text-muted-foreground sm:inline">
                    riesgo más alto: {String(RIESGO_LABEL[max]).toLowerCase()}
                  </span>
                )}
                <span className="h-px flex-1 bg-border" aria-hidden="true" />
                <ChevronDown size={18} aria-hidden="true"
                  className={`shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`} />
              </button>
            </h3>
            {open && (
              <ol id={panel} className="relative mb-3 ml-1 mt-2 before:absolute before:bottom-2 before:left-[5px] before:top-2 before:w-px before:bg-border">
                {g.evaluaciones.map((e) => (
                  <Evaluacion key={e.id} e={e} compacta={e.tipo === 'seguimiento' && e.id !== primera} esAdmin={esAdmin} nombres={nombres} onAnular={onAnular} />
                ))}
              </ol>
            )}
          </div>
        );
      })}
    </div>
  );
}
